import { AlfrescoApiService } from '@alfresco/adf-core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from 'environments/environment';

import { ssoConfig } from './sso.config';

export interface AlfrescoTicketResponse {
  ticket: string;
  userId: string;
  expiresIn: number;
  serverUrl: string;
}

// Tokens do SSO por aba, como o angular-oauth2-oidc faz nos outros apps (sessionStorage).
const ACCESS_TOKEN_KEY = 'sso_access_token';
const ID_TOKEN_KEY = 'sso_id_token';
const EXPIRES_AT_KEY = 'sso_expires_at';
const STATE_KEY = 'sso_state';
const NONCE_KEY = 'sso_nonce';
const VERIFIER_KEY = 'sso_code_verifier';
const LOGIN_ATTEMPTS_KEY = 'sso_login_attempts';

// Rotas que nao fazem sentido como destino depois do login.
const AUTH_ROUTES = ['/', '/login', '/sso-login', '/callback'];

const PKCE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

// O angular-oauth2-oidc com code flow + PKCE exige Angular 8+; o RM (Angular 5) faz o mesmo fluxo aqui.
@Injectable()
export class AuthSsoService {
  // Mensagem para a tela do /sso-login quando o retorno do SSO falha ou o login entra em ciclo.
  lastError: string = null;

  private initialized = false;
  private initPromise: Promise<boolean> = null;
  private ticketRecovery: Promise<boolean> = null;
  private appReady = false;

  constructor(private http: HttpClient, private injector: Injector) {
    // Guard, interceptor e telas antigas alcancam o servico por aqui, sem depender de DI.
    (window as any).authSsoInstance = this;
  }

  initialize(): Promise<boolean> {
    if (this.initialized) {
      return Promise.resolve(this.isLoggedIn());
    }
    // Varias rotas chamam initialize() ao mesmo tempo; uma promise so evita trocar o code duas vezes.
    if (!this.initPromise) {
      this.initPromise = this.doInitialize().then(
        (result) => {
          this.initPromise = null;
          return result;
        },
        (e) => {
          this.initPromise = null;
          throw e;
        }
      );
    }
    return this.initPromise;
  }

  private async doInitialize(): Promise<boolean> {
    if (this.isCallbackUrl()) {
      try {
        await this.completeLogin();
      } catch (e) {
        console.error('Falha ao concluir o login pelo SSO', e);
        this.lastError = 'Não foi possível concluir a autenticação pelo SSO.';
        // Sem o code na URL, um F5 nesta tela nao tenta trocar o mesmo code de novo.
        history.replaceState(null, '', this.appUrl('/callback'));
      }
    }

    if (this.isLoggedIn()) {
      sessionStorage.setItem('tokenAuth', 'Bearer ' + this.getAccessToken());
      try {
        await this.loadAlfrescoTicket();
        // So com o ticket a entrada esta completa; um token sem ticket ainda conta como tentativa (evita o ciclo).
        sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
      } catch (e) {
        console.error('Falha ao obter o ticket do Alfresco via SSO', e);
        if (e && e.status === 401) {
          // O SSO ja tentou renovar o ticket e nao conseguiu: sem ticket o RM abriria sem o usuario.
          this.logout(true);
          return new Promise<boolean>(() => {});
        }
      }
      // As authorities sao cifradas com o ticket: sem ele nao ha o que guardar.
      if (sessionStorage.getItem('ticket-ECM')) {
        try {
          await this.loadAuthorities();
        } catch (e) {
          console.error('Falha ao carregar as permissoes do RM', e);
        }
      }
    }

    this.initialized = true;
    return this.isLoggedIn();
  }

  // Chamado pelo AppComponent, depois dos APP_INITIALIZERs: so entao o AlfrescoApiService do ADF ja leu o app.config.json.
  onAppReady(): void {
    if (this.appReady) {
      return;
    }
    this.appReady = true;
    const api = this.alfrescoApi();
    if (!api) {
      return;
    }
    const ticket = this.getAlfrescoTicket();
    if (ticket && api.getTicketEcm() !== ticket) {
      api.setTicket(ticket, null);
    }
    // Um 401 do Alfresco nas telas do ADF (lista de documentos, visualizador) zera o ticket do js-api: o SSO devolve outro.
    ['ecmClient', 'ecmPrivateClient', 'searchClient'].forEach((name) => {
      const client = api[name];
      if (client && client.on) {
        client.on('unauthorized', () => {
          if (this.hasSsoSession()) {
            this.recoverAlfrescoTicket();
          }
        });
      }
    });
  }

  // Leva ao login do SSO, que volta sem pedir senha enquanto a sessao dele vale.
  // Devolve false quando o login entrou em ciclo: a tela do /sso-login mostra o lastError.
  login(route?: string): boolean {
    if (route) {
      sessionStorage.setItem('routerpast', route);
    }
    if (!this.registerLoginAttempt()) {
      this.lastError = 'O login pelo SSO foi interrompido para evitar um ciclo de redirecionamentos.';
      return false;
    }
    const state = randomString(32);
    const nonce = randomString(32);
    const verifier = randomString(64);
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(NONCE_KEY, nonce);
    sessionStorage.setItem(VERIFIER_KEY, verifier);

    window.location.href = ssoConfig.authorizeUrl + '?' + toQuery({
      response_type: 'code',
      client_id: ssoConfig.clientId,
      redirect_uri: ssoConfig.redirectUri,
      scope: ssoConfig.scope,
      state: state,
      nonce: nonce,
      code_challenge: pkceChallenge(verifier),
      code_challenge_method: 'S256'
    });
    return true;
  }

  // O botao "Entrar novamente" da tela de erro libera um novo ciclo de tentativas.
  retryLogin(): boolean {
    sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    this.lastError = null;
    return this.login();
  }

  // Logout global: o end_session do SSO encerra a sessao dele e o ticket do Alfresco em todas as aplicacoes.
  logout(expired?: boolean): void {
    const idToken = sessionStorage.getItem(ID_TOKEN_KEY);
    this.clearLocalSession();
    if (idToken) {
      window.location.href = ssoConfig.endSessionUrl + '?' + toQuery({
        id_token_hint: idToken,
        post_logout_redirect_uri: expired ? ssoConfig.expiredRedirectUri : ssoConfig.logoutRedirectUri,
        client_id: ssoConfig.clientId
      });
    } else {
      // Sem id_token o SSO recusa o end_session; o portal pede a confirmacao e sai por la.
      window.location.href = ssoConfig.issuer + '/?logout=confirm';
    }
  }

  // Apaga a sessao desta aba sem sair do SSO (ex.: 58 min parado): a proxima rota refaz a entrada pelo SSO.
  clearLocalSession(): void {
    sessionStorage.clear();
    localStorage.removeItem('ticket-ECM');
    localStorage.removeItem('ACS_USERNAME');
    localStorage.removeItem('APS_USERNAME');
    document.cookie = 'ALFRESCO_REMEMBER_ME=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    if (this.appReady) {
      const api = this.alfrescoApi();
      if (api) {
        api.setTicket(null, null);
      }
    }
  }

  // Um 401 do Alfresco com sessao SSO: busca o ticket de novo e, se o SSO tambem recusar, a sessao acabou.
  // failedTicket e o ticket que levou o 401, quando quem chama sabe qual foi.
  recoverAlfrescoTicket(route?: string, failedTicket?: string): Promise<boolean> {
    if (this.ticketRecovery) {
      return this.ticketRecovery;
    }
    const current = this.getAlfrescoTicket();
    // Uma tela dispara varias chamadas juntas: os 401 que chegam depois da troca, com o ticket antigo, ja estao resolvidos.
    if (failedTicket && failedTicket !== current) {
      return Promise.resolve(true);
    }
    if (!this.isLoggedIn()) {
      // Sem access token valido nao da pra pedir o ticket, e o SSO nao emite refresh token para SPA (client publico):
      // refaz o login pelo SSO, que volta sem senha enquanto a sessao dele vale, e a abertura renova o ticket.
      if (this.login(route || this.currentRoute())) {
        this.ticketRecovery = new Promise<boolean>(() => {});
        return this.ticketRecovery;
      }
      // Login em ciclo: sem o ticket vencido, o /sso-login mostra o erro em vez de voltar para a rota.
      sessionStorage.removeItem('ticket-ECM');
      localStorage.removeItem('ticket-ECM');
      window.location.href = this.appUrl('/sso-login');
      return Promise.resolve(false);
    }
    this.ticketRecovery = this.loadAlfrescoTicket().then(
      () => {
        if (this.getAlfrescoTicket() !== current) {
          this.ticketRecovery = null;
        } else {
          // O SSO devolveu o mesmo ticket: segura novas tentativas por um tempo em vez de pedir de novo a cada 401.
          setTimeout(() => (this.ticketRecovery = null), 10000);
        }
        // As permissoes ficam cifradas com o ticket: se a troca falhou, busca de novo.
        if (!sessionStorage.getItem('authority')) {
          this.loadAuthorities().catch((e) => console.error('Falha ao recarregar as permissoes do RM', e));
        }
        return true;
      },
      (e) => {
        // So o 401 quer dizer que o SSO nao tem mais como renovar; falha de rede nao derruba a sessao.
        if (e && e.status === 401) {
          this.logout(true);
          return new Promise<boolean>(() => {});
        }
        this.ticketRecovery = null;
        return false;
      }
    );
    return this.ticketRecovery;
  }

  isLoggedIn(): boolean {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const expiresAt = Number(sessionStorage.getItem(EXPIRES_AT_KEY));
    // Folga de 10 s: um token vencendo agora nao serve para a proxima chamada.
    return !!token && expiresAt - 10000 > Date.now();
  }

  // Entrou pelo SSO nesta aba: o id_token fica guardado ate o logout, mesmo depois que o access token vence.
  hasSsoSession(): boolean {
    return !!sessionStorage.getItem(ID_TOKEN_KEY);
  }

  getAccessToken(): string {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getIdentityClaims(): any {
    return decodeJwt(sessionStorage.getItem(ID_TOKEN_KEY));
  }

  getUsername(): string {
    const claims = this.getIdentityClaims();
    return (claims && (claims.preferred_username || claims.sub)) || localStorage.getItem('ACS_USERNAME');
  }

  // 'ticket-ECM' guarda o ticket cru: o ADF le do localStorage, as telas e os services do RM do sessionStorage.
  getAlfrescoTicket(): string {
    const ticket = sessionStorage.getItem('ticket-ECM');
    if (ticket) {
      return ticket;
    }
    // Varias telas do RM limpam o sessionStorage quando algo falha; o ticket do ADF continua no localStorage.
    const stored = localStorage.getItem('ticket-ECM');
    if (stored) {
      sessionStorage.setItem('ticket-ECM', stored);
    }
    return stored;
  }

  // Rota guardada antes de ir ao SSO (guard, inatividade); some depois de usada.
  consumeSavedRoute(): string {
    const route = sessionStorage.getItem('routerpast');
    sessionStorage.removeItem('routerpast');
    if (!route || AUTH_ROUTES.indexOf(route.split('?')[0]) !== -1) {
      return '/home';
    }
    return route;
  }

  // Troca o access token por um ticket do Alfresco, que e o que o ADF e a pcdoc-api entendem.
  loadAlfrescoTicket(): Promise<void> {
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.getAccessToken());
    return this.http
      .get<AlfrescoTicketResponse>(ssoConfig.alfrescoTicketUrl, { headers: headers })
      .toPromise()
      .then((res) => {
        if (res && res.userId) {
          this.applyUsername(res.userId);
        }
        if (res && res.ticket) {
          this.applyTicket(res.ticket);
        }
      });
  }

  private completeLogin(): Promise<void> {
    const params = parseQuery(window.location.search);
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const nonce = sessionStorage.getItem(NONCE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    // O state, o nonce e o verifier valem para uma volta so.
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(NONCE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);

    if (params['error']) {
      return Promise.reject(params['error_description'] || params['error']);
    }
    if (!params['code'] || !verifier || params['state'] !== expectedState) {
      return Promise.reject('Retorno do SSO sem code ou com state diferente do enviado');
    }

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    const body = toQuery({
      grant_type: 'authorization_code',
      code: params['code'],
      redirect_uri: ssoConfig.redirectUri,
      client_id: ssoConfig.clientId,
      code_verifier: verifier
    });
    return this.http
      .post<any>(ssoConfig.tokenUrl, body, { headers: headers })
      .toPromise()
      .then((res) => {
        const claims = decodeJwt(res && res.id_token);
        if (!res || !res.access_token || !claims || claims.nonce !== nonce) {
          throw new Error('Resposta do token endpoint invalida (access_token, id_token ou nonce)');
        }
        sessionStorage.setItem(ACCESS_TOKEN_KEY, res.access_token);
        sessionStorage.setItem(ID_TOKEN_KEY, res.id_token);
        sessionStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + (res.expires_in || 0) * 1000));
        this.lastError = null;
        // Tira o code da URL e ja parte da rota salva: o router so le a URL depois dos APP_INITIALIZERs.
        history.replaceState(null, '', this.appUrl(this.consumeSavedRoute()));
      });
  }

  // Mesmo dado que o login antigo guardava: as authorities do usuario, cifradas com o ticket, que e como as telas leem.
  private loadAuthorities(): Promise<void> {
    const username = this.getUsername();
    if (!username) {
      return Promise.resolve();
    }
    return this.http
      .get<any[]>(environment.APIpublic + '/get-user-userid?userId=' + encodeURIComponent(username))
      .toPromise()
      .then((list) => {
        this.setEncrypted('authority', (list || []).map((item) => item.authority));
        return this.loadCompanyAuthorities();
      });
  }

  // Link direto para uma tela de empresa (o guard guarda o id da rota): o login antigo ja carregava as permissoes dela.
  private loadCompanyAuthorities(): Promise<void> {
    const params = sessionStorage.getItem('params');
    if (!params || params === 'undefined' || params === 'null') {
      return Promise.resolve();
    }
    let clienteId: any = params;
    if (params.length >= 20) {
      try {
        clienteId = JSON.parse(CryptoJS.AES.decrypt(decodeURIComponent(params), 'pcdoc').toString(CryptoJS.enc.Utf8));
      } catch (e) {
        return Promise.resolve();
      }
    }
    return this.http
      .get<any[]>(environment.API + '/permission/get-permission-por-cliente/' + clienteId)
      .toPromise()
      .then(
        (list) => this.setEncrypted('authority-Empresa', (list || []).map((item) => item.authority)),
        (e) => console.error('Falha ao carregar as permissoes da empresa', e)
      );
  }

  private applyTicket(ticket: string): void {
    const previous = sessionStorage.getItem('ticket-ECM') || localStorage.getItem('ticket-ECM');
    if (previous && previous !== ticket) {
      this.reencryptAuthorities(previous, ticket);
    }
    sessionStorage.setItem('ticket-ECM', ticket);
    localStorage.setItem('ticket-ECM', ticket);
    // O ADF so considera o ECM logado (isEcmLoggedIn) com este cookie, que o login antigo gravava.
    document.cookie = 'ALFRESCO_REMEMBER_ME=1; path=/';
    if (this.appReady) {
      const api = this.alfrescoApi();
      if (api) {
        api.setTicket(ticket, null);
      }
    }
  }

  private applyUsername(username: string): void {
    // O login antigo deixava o usuario aqui (ADF) e as telas do RM ainda leem estas chaves.
    localStorage.setItem('ACS_USERNAME', username);
    localStorage.setItem('APS_USERNAME', username);
    sessionStorage.setItem('ACS_USERNAME', username);
  }

  // As telas decifram as authorities com o ticket atual e, se nao conseguem, limpam o sessionStorage inteiro:
  // um ticket renovado pelo SSO precisa levar as authorities junto.
  private reencryptAuthorities(previous: string, next: string): void {
    ['authority', 'authority-Empresa'].forEach((key) => {
      const stored = sessionStorage.getItem(key);
      if (!stored) {
        return;
      }
      try {
        const plain = CryptoJS.AES.decrypt(stored, previous).toString(CryptoJS.enc.Utf8);
        JSON.parse(plain);
        sessionStorage.setItem(key, CryptoJS.AES.encrypt(plain, next).toString());
      } catch (e) {
        sessionStorage.removeItem(key);
      }
    });
  }

  private setEncrypted(key: string, value: any): void {
    const ticket = this.getAlfrescoTicket();
    if (ticket) {
      sessionStorage.setItem(key, CryptoJS.AES.encrypt(JSON.stringify(value), ticket).toString());
    }
  }

  // Ate 3 idas ao SSO em 2 minutos: mais que isso e um ciclo (retorno que nao conclui), e o RM para de redirecionar.
  private registerLoginAttempt(): boolean {
    const now = Date.now();
    let attempts: number[] = [];
    try {
      attempts = JSON.parse(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) || '[]');
    } catch (e) {
      attempts = [];
    }
    attempts = attempts.filter((time) => now - time < 120000);
    if (attempts.length >= 3) {
      return false;
    }
    attempts.push(now);
    sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
    return true;
  }

  private isCallbackUrl(): boolean {
    const search = window.location.search;
    return /\/callback\/?$/.test(window.location.pathname) && (search.indexOf('code=') !== -1 || search.indexOf('error=') !== -1);
  }

  private currentRoute(): string {
    const base = ssoConfig.baseHref.replace(/\/$/, '');
    const path = window.location.pathname;
    const route = base && path.indexOf(base) === 0 ? path.substring(base.length) : path;
    return (route || '/') + window.location.search;
  }

  private appUrl(route: string): string {
    return ssoConfig.baseHref.replace(/\/$/, '') + route;
  }

  private alfrescoApi(): any {
    try {
      return this.injector.get(AlfrescoApiService).getInstance();
    } catch (e) {
      return null;
    }
  }
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  let result = '';
  for (let i = 0; i < bytes.length; i++) {
    result += PKCE_CHARS.charAt(bytes[i] % PKCE_CHARS.length);
  }
  return result;
}

// S256 do PKCE com o crypto-js, que o RM ja usa: o crypto.subtle so existe em contexto seguro.
function pkceChallenge(verifier: string): string {
  return CryptoJS.SHA256(verifier)
    .toString(CryptoJS.enc.Base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function decodeJwt(token: string): any {
  if (!token) {
    return null;
  }
  try {
    let payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) {
      payload += '=';
    }
    return JSON.parse(CryptoJS.enc.Base64.parse(payload).toString(CryptoJS.enc.Utf8));
  } catch (e) {
    return null;
  }
}

function toQuery(params: { [key: string]: string }): string {
  return Object.keys(params)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
    .join('&');
}

function parseQuery(search: string): { [key: string]: string } {
  const result: { [key: string]: string } = {};
  search
    .replace(/^\?/, '')
    .split('&')
    .forEach((pair) => {
      if (!pair) {
        return;
      }
      const index = pair.indexOf('=');
      const key = decodeURIComponent(index === -1 ? pair : pair.substring(0, index));
      const value = index === -1 ? '' : decodeURIComponent(pair.substring(index + 1).replace(/\+/g, ' '));
      result[key] = value;
    });
  return result;
}

// APP_INITIALIZER: sobe o SSO antes das rotas, para guard, interceptor e services o encontrarem pronto num F5
// ou na volta do /callback.
export function initializeSso(authSso: AuthSsoService) {
  return () => authSso.initialize();
}



// WEBPACK FOOTER //
// /app/src/app/sso/auth-sso.service.ts